<template>
  <BaseMenu
    :close-on-content-click="false"
    offset-y
    bottom
    :nudge-bottom="4"
    @input="onMenuToggle"
  >
    <template #activator="{ on, attrs }">
      <BaseButton
        :outlined="value === null"
        :type="value !== null ? 'primary' : 'secondary'"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon small class="mr-1">{{ mdiAccountTie }}</v-icon>
        {{ selectedLabel }}
        <v-icon small class="ml-1">{{ mdiChevronDown }}</v-icon>
      </BaseButton>
    </template>
    <template #content>
      <div class="filter_menu_content">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          :placeholder="$t('folder.filters.searchPlaceholder')"
          prepend-inner-icon="$mdiMagnify"
          class="search_field"
          autofocus
        />
        <div
          class="collaborator_item"
          :class="{ selected: value === null }"
          @click="$emit('input', null)"
        >
          {{ $t("folder.filters.allCollaborators") }}
        </div>
        <template v-if="!fetching">
          <div
            v-for="collaborator in collaborators"
            :key="collaborator.id"
            class="collaborator_item"
            :class="{ selected: value === collaborator.id }"
            @click="selectCollaborator(collaborator)"
          >
            {{ collaborator.user.email }}
            <span
              v-if="collaborator.id === currentCollaboratorId"
              class="me_label"
              >{{ $t("folder.filters.currentUser") }}</span
            >
          </div>
          <div v-if="!collaborators.length" class="no_result">
            {{ $t("folder.filters.noResult") }}
          </div>
        </template>
        <div v-else class="loading">
          <v-progress-circular
            indeterminate
            size="20"
            width="2"
            color="primary"
          />
        </div>
      </div>
    </template>
  </BaseMenu>
</template>

<script>
import { mdiAccountTie, mdiChevronDown } from "@mdi/js";
import WorkflowAPI from "@/modules/Admin/Services/workflow.api";

export default {
  name: "WorkflowFolderCollaboratorFilter",
  props: {
    value: {
      type: Number,
      default: null,
    },
    organizationId: {
      type: [Number, String],
      required: true,
    },
    skipDefault: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mdiAccountTie,
      mdiChevronDown,
      search: "",
      collaborators: [],
      fetching: false,
      searchTimeoutRef: null,
      currentCollaboratorId: null,
      selectedCollaboratorEmail: null,
    };
  },
  computed: {
    selectedLabel() {
      if (this.value === null)
        return this.$t("folder.filters.allCollaborators");
      if (this.value === this.currentCollaboratorId)
        return this.$t("folder.filters.myFolders");
      const found = this.collaborators.find((c) => c.id === this.value);
      if (found) return found.user.email;
      return (
        this.selectedCollaboratorEmail ?? this.$t("folder.filters.collaborator")
      );
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimeoutRef);
      this.searchTimeoutRef = setTimeout(() => {
        this.fetchCollaborators();
      }, 300);
    },
  },
  async created() {
    await this.fetchCollaborators();
    const currentUserId = this.$store.state.account.user?.id;
    const current = this.collaborators.find((c) => c.user.id === currentUserId);
    if (current) {
      this.currentCollaboratorId = current.id;
      if (this.value === null && !this.skipDefault)
        this.$emit("input", current.id);
      this.$emit("init", current.id);
    }
  },
  methods: {
    selectCollaborator(collaborator) {
      this.selectedCollaboratorEmail = collaborator.user.email;
      this.$emit("input", collaborator.id);
    },
    async fetchCollaborators() {
      this.fetching = true;
      try {
        const params = this.search ? { email: this.search } : {};
        const res = await WorkflowAPI.getCollaboratorsOrganization(
          this.organizationId,
          params
        );
        this.collaborators = res.data.results ?? res.data;
      } catch {
        // silently fail
      }
      this.fetching = false;
    },
    onMenuToggle(isOpen) {
      if (!isOpen) {
        clearTimeout(this.searchTimeoutRef);
        this.search = "";
        this.fetchCollaborators();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.filter_menu_content {
  padding: 8px;
  min-width: 280px;
  background: white;

  .search_field {
    margin-bottom: 4px;
  }
}

.collaborator_item {
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;

  &:hover {
    background: #f4f7ff;
  }

  &.selected {
    background: #e6eff4;
    font-weight: 600;
  }
}

.me_label {
  margin-left: 4px;
  font-size: 12px;
  color: #9e9e9e;
}

.no_result,
.loading {
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: #9e9e9e;
}
</style>
